import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { add, isBefore, set } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import { Header } from '../../components/header';
import {
  dateFormatWeek,
  TimelineHeaderNav,
  viewPeriodWeek,
} from '../../components/planner/partials/timelineHeader';
import { Page } from '../../shared/components/page';
import { useDate } from '../../core/hooks/useDate';
import { useGetWeekPlansQuery, useUpdateWeekPlanMutation } from '../../core/redux/worker';
import { setShowOnlyActive, setShowOnlyPlanned, useOrganizations } from '../../core/redux/leftFilterState';
import WeekPlannerTable from '../../components/weekPlanner/WeekPlannerTable';
import { WeekPlan } from '../../core/types/weekPlan';
import { useHasRoles } from '../../shared/msal';
import Summary from '../../components/weekPlanner/Summary';
import { RootState } from '../../core/redux';

export const WeekPlanner = () => {
  const [date, setDate] = useDate('weekPlanner');

  const filters = useSelector((state: RootState) => state.leftFilterState.weekPlanner);
  const { showOnlyPlanned, showOnlyActive } = filters;

  const organizations = useOrganizations();

  const { data, isFetching } = useGetWeekPlansQuery({
    date,
    organizations,
    showOnlyPlanned,
  });

  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((plan) => (showOnlyActive ? plan.isActive : true));
  }, [data, showOnlyActive]);

  const [updateWeekPlans] = useUpdateWeekPlanMutation();

  const dispatch = useDispatch();

  const handleRowUpdate = async (newPlan: WeekPlan): Promise<WeekPlan> => {
    const nonNumberKeys = ['id', 'projectName', 'comment', 'date', 'isActive', 'site'];
    const fixedNewRow: WeekPlan = Object.fromEntries(
      Object.entries(newPlan).map(([key, value]) => [
        key,
        nonNumberKeys.includes(key) ? value : Number(value),
      ]),
    ) as WeekPlan;
    updateWeekPlans(fixedNewRow);
    return newPlan;
  };

  const isSuperWriter = useHasRoles(['admin', 'super-admin', 'cars-coordinator']);
  const isWriter = useHasRoles(['cars-writer']);

  const isBeforeDeadline = () => {
    const thursdayLastWeek = add(date, { days: -4 });
    const deadLine = set(thursdayLastWeek, { hours: 7 });
    return isBefore(new Date(), deadLine);
  };

  const canEdit = isSuperWriter || (isBeforeDeadline() && isWriter);

  return (
    <Page className="transportplanlegger-page">
      <Header page="weekPlanner" />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap="0.5rem">
          <div className="timeline-header-component">
            <TimelineHeaderNav
              dateFormat={dateFormatWeek}
              viewPeriod={viewPeriodWeek}
              onDayChange={setDate}
              day={date}
            />
          </div>
          {!canEdit && <LockIcon style={{ color: '#f5bc1d' }} />}
        </Box>
        <Box display="flex" alignItems="center" gap="0.5rem">
          <FormControlLabel
            label="Vis kun planlagte"
            control={(
              <Checkbox
                checked={showOnlyPlanned}
                onChange={() => dispatch(setShowOnlyPlanned(!showOnlyPlanned))}
              />
            )}
          />
          <FormControlLabel
            label="Vis kun aktive"
            control={(
              <Checkbox
                checked={showOnlyActive}
                onChange={() => dispatch(setShowOnlyActive(!showOnlyActive))}
              />
            )}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        alignItems="center"
        padding="1rem"
        gap="1rem"
      >
        {isFetching && <CircularProgress />}
        {!isFetching && data && <Summary data={filteredData} />}
        {!isFetching && data && (
          <WeekPlannerTable data={filteredData} handleRowUpdate={handleRowUpdate} isEditable={canEdit} />
        )}
      </Box>
    </Page>
  );
};
